import React from 'react';
import {
  Heart,
  Baby,
  MapPin,
  AlertTriangle,
  Candy,
  Calendar,
  Weight,
  Target,
  Venus,
  Mars,
  Users,
} from 'lucide-react';
import { Button, Typography, Card, Row, Col } from 'antd';
import Modal from '../modal';

const { Title, Text, Paragraph } = Typography;

interface BlindSweepReportProps {
  predictionResults: any;
  handleReportModalClose: () => void;
  isOpen: boolean;
  onClose: () => void;
}

function BlindSweepReport({
  predictionResults,
  handleReportModalClose,
  isOpen,
  onClose,
}: BlindSweepReportProps) {
  const results = predictionResults;
  const hasResults = true;
  console.log('results ::', results);
  // Helper function to check if a card has a valid value
  const hasValue = (value: any): boolean => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'number') return value > 0;
    if (typeof value === 'string') {
      if (value === '0' || value === '') return false;
    }
    return true;
  };

  // Card style based on whether it has a value
  const getCardStyle = (value: any) => {
    const baseStyle = {
      height: '100%',
      borderRadius: '8px',
    };

    if (!hasResults || !hasValue(value)) {
      return {
        ...baseStyle,
        opacity: 0.6,
        filter: 'blur(1px)',
        pointerEvents: 'none',
      };
    }

    return baseStyle;
  };

  // Card component to avoid repetition
  const MetricCard = ({
    title,
    value,
    description,
    icon,
    unit = '',
    secondaryValue = null,
    secondaryDescription = null,
    iconColor,
  }: {
    title: string;
    value: any;
    description: string;
    icon: React.ReactNode;
    unit?: string;
    secondaryValue?: any;
    secondaryDescription?: string | null;
    iconColor: string;
  }) => (
    <Card style={getCardStyle(value)}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '8px',
        }}
      >
        <Text strong style={{ fontSize: '14px' }}>
          {title}
        </Text>
        <span style={{ color: iconColor }}>{icon}</span>
      </div>
      <Title level={3} style={{ margin: '0', marginBottom: '4px' }}>
        {hasResults && hasValue(value) ? `${value} ${unit}` : '***'}
      </Title>
      <Text type="secondary" style={{ fontSize: '12px' }}>
        {description}
      </Text>
      {secondaryValue && hasResults && hasValue(secondaryValue) && (
        <div style={{ marginTop: '4px' }}>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {secondaryValue} {secondaryDescription}
          </Text>
        </div>
      )}
    </Card>
  );

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="extraLarge"
        overlayClassName="py-10 top-0 left-0 overflow-hidden"
        className="flex h-[90vh] flex-col bg-card-color-100"
      >
        <div className='p-5'>
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <Title level={2} style={{ marginBottom: '8px' }}>
              Ultrasound Analysis Report
            </Title>
            <Text style={{ fontSize: '18px', color: '#666' }}>
              Video Prediction Results
            </Text>
          </div>

          <Row gutter={[24, 24]}>
            <Col xs={24} sm={12} md={8}>
              <MetricCard
                title="Heart Rate"
                value={results?.Heart_rate}
                unit="BPM"
                description="Fetal cardiac activity measurement"
                icon={<Heart size={24} />}
                iconColor="#f5222d"
              />
            </Col>

            <Col xs={24} sm={12} md={8}>
              <MetricCard
                title="Fetal Presentation"
                value={results?.fetal_presentation}
                description="Position of the fetus in the uterus"
                icon={<Baby size={24} />}
                iconColor="#1890ff"
              />
            </Col>

            <Col xs={24} sm={12} md={8}>
              <MetricCard
                title="Placenta Localization"
                value={results?.Placenta_localization}
                description="Location of the placenta within the uterus"
                icon={<MapPin size={24} />}
                iconColor="#52c41a"
              />
            </Col>

            <Col xs={24} sm={12} md={8}>
              <MetricCard
                title="Twin Pregnancy"
                value={results?.is_twin ? 'Yes' : 'No'}
                description="Indicates presence of multiple fetuses"
                icon={<Users size={24} />}
                iconColor="#eb2f96"
              />
            </Col>

            <Col xs={24} sm={12} md={8}>
              <MetricCard
                title="Preeclampsia"
                value={results?.preeclampsia}
                description="Pregnancy complication with high blood pressure"
                icon={<AlertTriangle size={24} />}
                iconColor="#faad14"
              />
            </Col>

            <Col xs={24} sm={12} md={8}>
              <MetricCard
                title="Gestational Diabetes"
                value={results?.gestational_diabetes}
                description="High blood sugar during pregnancy"
                icon={<Candy size={24} />}
                iconColor="#722ed1"
              />
            </Col>

            <Col xs={24} sm={12} md={8}>
              <MetricCard
                title="Gestational Age"
                value={results?.gestational_age_days}
                description="Duration of pregnancy"
                secondaryValue={results?.gestational_age_days}
                secondaryDescription="days total"
                icon={<Calendar size={24} />}
                iconColor="#2f54eb"
              />
            </Col>

            <Col xs={24} sm={12} md={8}>
              <MetricCard
                title="Fetal Weight"
                value={results?.fetal_weight}
                unit="g"
                description="Estimated fetal weight in grams"
                icon={<Weight size={24} />}
                iconColor="#13c2c2"
              />
            </Col>

            <Col xs={24} sm={12} md={8}>
              <MetricCard
                title="Placenta Distance"
                value={results?.placenta_distance}
                description="Distance from cervix"
                secondaryValue={results?.placenta_distance_cm}
                secondaryDescription="cm"
                icon={<Target size={24} />}
                iconColor="#fa8c16"
              />
            </Col>

            <Col xs={24} sm={12} md={8}>
              <MetricCard
                title="Fetal Sex"
                value={results?.fetal_sex}
                description="Biological sex of the fetus"
                icon={
                  results?.fetal_sex === 'Male' ? (
                    <Mars size={24} />
                  ) : (
                    <Venus size={24} />
                  )
                }
                iconColor={
                  results?.fetal_sex === 'Male' ? '#1890ff' : '#eb2f96'
                }
              />
            </Col>
          </Row>

          <div
            style={{
              marginTop: '24px',
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <Button type="primary" onClick={handleReportModalClose}>
              Close Report
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}

export default BlindSweepReport;
